/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Stored metadata about a tool exposed by an MCP server. The annotation hints mirror MCP tool annotations and drive annotation-aware authorization.
 */
export type ToolMetadata = {
  /**
   * When the tool metadata entry was created
   */
  createdAt: Date;
  /**
   * When the tool metadata entry was deleted. Only present on deleted entries returned by listToolMetadata with include_deleted.
   */
  deletedAt?: Date | undefined;
  /**
   * Hint that the tool may perform destructive updates to its environment
   */
  destructiveHint?: boolean | undefined;
  /**
   * Hint that calling the tool repeatedly with the same arguments has no additional effect
   */
  idempotentHint?: boolean | undefined;
  /**
   * The ID of the MCP server the tool metadata belongs to
   */
  mcpServerId: string;
  /**
   * Hint that the tool may interact with an open world of external entities
   */
  openWorldHint?: boolean | undefined;
  /**
   * Hint that the tool does not modify its environment
   */
  readOnlyHint?: boolean | undefined;
  /**
   * A human-readable title for the tool
   */
  title?: string | undefined;
  /**
   * The name of the tool
   */
  toolName: string;
  /**
   * When the tool metadata entry was last updated
   */
  updatedAt: Date;
};

/** @internal */
export const ToolMetadata$inboundSchema: z.ZodMiniType<ToolMetadata, unknown> =
  z.pipe(
    z.object({
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      deleted_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      destructive_hint: z.optional(z.boolean()),
      idempotent_hint: z.optional(z.boolean()),
      mcp_server_id: z.string(),
      open_world_hint: z.optional(z.boolean()),
      read_only_hint: z.optional(z.boolean()),
      title: z.optional(z.string()),
      tool_name: z.string(),
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
    }),
    z.transform((v) => {
      return remap$(v, {
        "created_at": "createdAt",
        "deleted_at": "deletedAt",
        "destructive_hint": "destructiveHint",
        "idempotent_hint": "idempotentHint",
        "mcp_server_id": "mcpServerId",
        "open_world_hint": "openWorldHint",
        "read_only_hint": "readOnlyHint",
        "tool_name": "toolName",
        "updated_at": "updatedAt",
      });
    }),
  );

export function toolMetadataFromJSON(
  jsonString: string,
): SafeParseResult<ToolMetadata, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ToolMetadata$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ToolMetadata' from JSON`,
  );
}
